import type { ReactNode } from 'react'
import { View } from 'react-native'
import type { GestureResponderEvent } from 'react-native'

import { Card, CardBody } from '@/src/elements/card'
import { CheckStamp } from '@/src/elements/checkstamp/Checkstamp'
import { Icon } from '@/src/elements/media/Icon'
import { COMMUNITY_LIFETIME } from '@/src/features/community/models/community.constants'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'

import type { TPublicationApi } from '../../../api/types/getPublication.types'
import type { TMergePublicationTypes, TPickPublication } from '../../../models/publication.types'
import { PublicationDetails, type TPublicationDetailsProps } from '../PublicationDetails'
import {
  PublicationShareCommentButtons,
  type TPublicationShareCommentButtonsProps,
} from '../PublicationShareCommentButtons'

export type TPublicationItemBaseProps = {
  isNextToBePrayed?: boolean
  publication: TMergePublicationTypes<
    [
      TPublicationShareCommentButtonsProps['publication'],
      TPublicationDetailsProps['publication'],
      TPickPublication<TPublicationApi, 'hasPrayed' | 'community.nbPublications' | 'community.nbTotalPublications'>,
    ]
  >
  showPublicationDate?: boolean
  onSharePress?: (e?: GestureResponderEvent | React.MouseEvent) => void
  onCommentPress?: (e?: GestureResponderEvent | React.MouseEvent) => void
} & Record<'onCardPress', (e?: GestureResponderEvent | React.MouseEvent) => void>

export const PublicationItemBase = (props: TPublicationItemBaseProps): ReactNode => {
  const { publication, onCardPress, onSharePress, onCommentPress, isNextToBePrayed } = props
  const { hasPrayed } = publication
  const isScheduledPublication = publication.isScheduled && !publication.published
  const showThread = publication.community?.lifetime !== COMMUNITY_LIFETIME.EVERLASTING
  const showCheckStamp =
    hasPrayed ||
    (isNextToBePrayed && publication.rank === publication.community.nbPublications) ||
    (isNextToBePrayed && publication.community.lifetime !== COMMUNITY_LIFETIME.EVERLASTING)

  return (
    <Card
      shadow="none"
      className="flex-row items-center bg-transparent web:pl-4 min-h-[135px]"
      testID={
        isScheduledPublication
          ? TEST_ID.COMMUNITY.PUBLICATION_ITEM.SCHEDULED
          : TEST_ID.COMMUNITY.PUBLICATION_ITEM.PUBLISHED
      }
      {...(!isScheduledPublication && { onPress: onCardPress, isPressable: true, additionalProps: { role: 'link' } })}
    >
      <View className="flex-col items-center web:h-full w-6 relative justify-center">
        {showThread && (
          <>
            <View
              className="w-full items-center h-1/2 absolute top-0 overflow-hidden"
              testID={TEST_ID.PUBLICATION_CARD.THREAD_TOP}
            >
              {publication.rank > 1 && <Icon name={ICON.DOTTED_LINE} className="h-auto w-0.5 absolute bottom-0" />}
            </View>
            <View
              className="w-full items-center h-1/2 absolute bottom-0 overflow-hidden"
              testID={TEST_ID.PUBLICATION_CARD.THREAD_BOTTOM}
            >
              {publication.rank !== publication.community.nbTotalPublications && (
                <Icon name={ICON.DOTTED_LINE} className="h-auto w-0.5 absolute top-0" />
              )}
            </View>
          </>
        )}
        {showCheckStamp && (
          <CheckStamp state={hasPrayed ? 'valid' : 'empty'} checkAnimationDelay={hasPrayed ? 750 : null} size="sm" />
        )}
      </View>

      <CardBody
        testID={isScheduledPublication ? TEST_ID.PUBLICATION_CARD.SCHEDULED : undefined}
        className="overflow-hidden pt-4 px-2 pb-2"
      >
        <PublicationDetails noDayPrefix publication={publication} variant="default" className="mt-5" />
        <PublicationShareCommentButtons
          publication={publication}
          onSharePress={onSharePress}
          onCommentPress={onCommentPress}
          colors="default"
        />
      </CardBody>
    </Card>
  )
}
